import type { PublicUser } from "@codvero/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { oauthApi } from "@/api/oauth.ts";
import { Alert, Button, PageTitle, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { AuthLayout } from "./AuthLayout.tsx";

/** OAuth consent for outside apps (ChatGPT, Claude, ...) that want to control Codvero over MCP. */
export function ConsentPage({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const query = location.search.replace(/^\?/, "");
  const params = Object.fromEntries(new URLSearchParams(query).entries());
  const info = useQuery({ queryKey: ["oauth", "consent", query], queryFn: () => oauthApi.consent(query), retry: false });
  const decide = useMutation({
    mutationFn: (approve: boolean) => oauthApi.decide({ client_id: params.client_id, redirect_uri: params.redirect_uri, scope: info.data?.scope ?? params.scope ?? "", state: params.state ?? "", code_challenge: params.code_challenge, code_challenge_method: params.code_challenge_method, ...(params.resource ? { resource: params.resource } : {}), approve }),
    onSuccess: (r) => {
      // defence in depth: never navigate to a script-capable URL on this origin
      let proto = "";
      try { proto = new URL(r.redirect).protocol; } catch { /* invalid */ }
      if (!proto || ["javascript:", "data:", "vbscript:", "file:", "blob:", "about:"].includes(proto)) return;
      location.href = r.redirect;
    },
  });
  return (
    <AuthLayout>
      <div className="space-y-5">
        <PageTitle title={t("consent.title")} subtitle={t("consent.subtitle", { email: user.email })} />
        {info.isPending && <Spinner />}
        {info.error && <Alert tone="danger">{toMsg(info.error)}</Alert>}
        {decide.error && <Alert tone="danger">{toMsg(decide.error)}</Alert>}
        {info.data && (
          <>
            <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3">
              {info.data.logoUri ? <img src={info.data.logoUri} alt="" className="size-10 rounded-md object-cover" /> : <span className="grid size-10 place-items-center rounded-md bg-accent-soft"><ShieldCheck className="size-5 text-accent" /></span>}
              <div className="min-w-0">
                <div className="font-medium">{info.data.clientName}</div>
                <div className="truncate text-xs text-fg-muted">{t("consent.redirectsTo")} <span dir="ltr" className="font-mono text-fg">{redirectLabel(info.data.redirectUri)}</span></div>
                {info.data.clientUri && <div className="truncate text-[11px] text-fg-muted">{t("consent.claims")} <span dir="ltr">{hostOf(info.data.clientUri)}</span></div>}
              </div>
            </div>
            <div>
              <div className="mb-1 text-sm font-medium">{t("consent.wants")}</div>
              <ul className="space-y-1 text-sm">
                {info.data.scopes.map((s, i) => <li key={s} className="flex items-start gap-2"><span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" />{t(`consent.scopes.${info.data!.scopeIds[i] ?? ""}`, { defaultValue: s })}</li>)}
              </ul>
            </div>
            <p className="text-xs text-fg-muted">{t("consent.note")}</p>
            <div className="flex gap-2">
              <Button className="flex-1" loading={decide.isPending && decide.variables === true} onClick={() => decide.mutate(true)}>{t("consent.approve")}</Button>
              <Button variant="secondary" className="flex-1" loading={decide.isPending && decide.variables === false} onClick={() => decide.mutate(false)}>{t("consent.deny")}</Button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

function hostOf(u: string): string {
  try { return new URL(u).host; } catch { return u; }
}
/** Where the code goes, as the user should judge it: the host for web URLs, the scheme for native apps. */
function redirectLabel(u: string): string {
  try { const p = new URL(u); return p.protocol === "https:" || p.protocol === "http:" ? p.host : `${p.protocol}//`; } catch { return u; }
}
